<template>
  <v-container>
    <v-row class="justify-center">
      <v-col cols="12" sm="11" lg="11" :class="['px-0']">
        <v-row>
          <!-- LEFT SIDE - Certificate Section -->
          <v-col cols="12" md="6" class="pr-md-2">
            <v-card class="glass-card">
              <!-- Certificate Header -->
              <div class="glass-header">
                <div class="d-flex align-center">
                  <v-icon class="header-icon mr-3" size="40" color="white">
                    mdi-certificate
                  </v-icon>
                  <h2 class="section-title">Certifikat za fiskalizaciju</h2>
                </div>
              </div>

              <!-- Certificate Content -->
              <v-card-text class="pa-6">
                <!-- Certificate exists -->
                <div v-if="certData">
                  <!-- Certificate Info -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="info-label">Naziv certifikata</div>
                      <div class="info-value">{{ certData.common_name }}</div>
                    </v-col>
                  </v-row>

                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="info-label">Serijski broj</div>
                      <div class="info-value serial-number">{{ certData.serial_num }}</div>
                    </v-col>
                  </v-row>

                  <v-row dense class="mb-4">
                    <v-col cols="6">
                      <div class="info-label">Vrijedi od</div>
                      <div class="info-value">{{ formatDate(certData.start_date) }}</div>
                    </v-col>
                    <v-col cols="6">
                      <div class="info-label">Vrijedi do</div>
                      <div class="info-value" :class="{ 'expired-date': isExpired }">
                        {{ formatDate(certData.exp_date) }}
                      </div>
                    </v-col>
                  </v-row>

                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="info-label">Lozinka</div>
                      <div class="d-flex align-center">
                        <div class="info-value password-field mr-2">
                          {{ passwordVisible ? certData.passwd : '••••••••••' }}
                        </div>
                        <v-btn
                          icon
                          small
                          @click="passwordVisible = !passwordVisible"
                          class="password-toggle">
                          <v-icon small :color="passwordVisible ? '#3989C9' : 'rgba(70, 70, 80, 0.5)'">
                            {{ passwordVisible ? 'mdi-eye' : 'mdi-eye-off' }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Status Badge -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <v-chip
                        :color="isExpired ? 'error' : 'success'"
                        text-color="white"
                        small
                        class="status-chip">
                        <v-icon small left>
                          {{ isExpired ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                        </v-icon>
                        {{ isExpired ? 'Istekao' : 'Aktivan' }}
                      </v-chip>
                    </v-col>
                  </v-row>

                  <!-- Upload New Certificate Button -->
                  <v-row dense>
                    <v-col cols="12">
                      <v-btn
                        class="upload-btn"
                        block
                        @click="openUploadDialog"
                        elevation="0">
                        <v-icon left small>mdi-upload</v-icon>
                        Ažuriraj certifikat
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>

                <!-- No Certificate -->
                <div v-else class="no-cert-content">
                  <v-icon class="no-cert-icon" size="80">
                    mdi-certificate-outline
                  </v-icon>
                  <h3 class="no-cert-title mt-4 mb-2">
                    Certifikat nije instaliran
                  </h3>
                  <p class="no-cert-text mb-5">
                    Dodajte certifikat za fiskalizaciju kako biste mogli izdavati fiskalne račune
                  </p>
                  <v-btn
                    class="upload-btn"
                    @click="openUploadDialog"
                    elevation="0">
                    <v-icon left small>mdi-upload</v-icon>
                    Dodaj certifikat
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- RIGHT SIDE - eRacun Section -->
          <v-col cols="12" md="6" class="pl-md-2">
            <v-card class="glass-card">
              <!-- eRacun Header -->
              <div class="glass-header">
                <div class="d-flex align-center">
                  <v-icon class="header-icon mr-3" size="40" color="white">
                    mdi-email-outline
                  </v-icon>
                  <h2 class="section-title">E-račun konfiguracija</h2>
                </div>
              </div>

              <!-- eRacun Content -->
              <v-card-text class="pa-6">
                <!-- Configuration exists -->
                <div v-if="eracunConfig">
                  <!-- Active Status Toggle -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="info-label">Status</div>
                          <div class="info-value-inline">
                            {{ eracunConfig.active ? 'Aktivno' : 'Neaktivno' }}
                          </div>
                        </div>
                        <v-switch
                          v-model="eracunConfig.active"
                          color="success"
                          :loading="updatingStatus"
                          @change="toggleActiveStatus"
                          hide-details
                        ></v-switch>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Provider -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="info-label">Pružatelj usluge</div>
                      <div class="info-value">{{ providerDisplayName }}</div>
                    </v-col>
                  </v-row>

                  <!-- API Key (placeholder) -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="info-label">API ključ</div>
                      <div class="info-value password-field">••••••••••••••••</div>
                      <div class="mt-2">
                        <v-btn
                          x-small
                          text
                          color="primary"
                          @click="openUpdateSecretDialog"
                        >
                          <v-icon left small>mdi-key-change</v-icon>
                          Promijeni API ključ
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>


                  <!-- Send PDF Visualisation Toggle -->
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="info-label">Slanje PDF vizualizacije</div>
                          <div class="info-value-inline">
                            {{ eracunConfig.send_visualisation ? 'Uključeno' : 'Isključeno' }}
                          </div>
                          <div class="info-hint">Ako je označeno, uz eRačun će se slati i PDF računa iz Aikase kao i HUB3 kod za skeniranje i plaćanje.</div>
                        </div>
                        <v-switch
                          v-model="eracunConfig.send_visualisation"
                          color="success"
                          :loading="updatingSendVisualisation"
                          @change="toggleSendVisualisation"
                          hide-details
                        ></v-switch>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row dense class="mb-4">
                    <v-col cols="12">
                      <v-chip
                        :color="eracunConfig.active ? 'success' : 'warning'"
                        text-color="white"
                        small
                        class="status-chip">
                        <v-icon small left>
                          {{ eracunConfig.active ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ eracunConfig.active ? 'Konfiguracija aktivna' : 'Konfiguracija neaktivna' }}
                      </v-chip>
                    </v-col>
                  </v-row>

                  <!-- Last Updated -->
                  <v-row dense v-if="eracunConfig.updated_at">
                    <v-col cols="12">
                      <div class="info-label-small">Zadnja izmjena</div>
                      <div class="info-value-small">{{ formatTimestamp(eracunConfig.updated_at) }}</div>
                    </v-col>
                  </v-row>
                </div>

                <!-- No Configuration -->
                <div v-else-if="!loadingConfig" class="no-cert-content">
                  <v-icon class="no-cert-icon" size="80">
                    mdi-cog-outline
                  </v-icon>
                  <h3 class="no-cert-title mt-4 mb-2">
                    E-račun nije konfiguriran
                  </h3>
                  <p class="no-cert-text mb-5">
                    Dodajte konfiguraciju e-računa kako biste omogućili slanje elektronskih računa
                  </p>
                  <v-btn
                    class="upload-btn"
                    @click="openCreateConfigDialog"
                    elevation="0">
                    <v-icon left small>mdi-plus</v-icon>
                    Dodaj konfiguraciju
                  </v-btn>
                </div>

                <!-- Loading State -->
                <div v-else class="text-center pa-8">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                  ></v-progress-circular>
                  <p class="mt-4 text--secondary">Učitavanje konfiguracije...</p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Certificate Upload Dialog -->
    <certificate-upload-form ref="certificateUpload" @uploaded="onCertificateUploaded" />

    <!-- Create/Update Config Dialog -->
    <v-dialog v-model="configDialog" max-width="520" persistent>
      <v-card class="config-dialog-card">
        <!-- Header with gradient -->
        <div class="config-dialog-header">
          <h2 class="config-dialog-title">
            {{ isCreatingConfig ? 'Dodaj E-račun konfiguraciju' : 'Ažuriraj API ključ' }}
          </h2>
        </div>

        <v-card-text class="pt-6 pb-4 px-6">
          <v-form ref="configForm" v-model="configFormValid">
            <!-- Provider Selection (only for create) -->
            <v-select
              v-if="isCreatingConfig"
              v-model="configForm.provider"
              :items="providerOptions"
              item-text="text"
              item-value="value"
              label="Pružatelj usluge *"
              outlined
              dense
              :rules="[v => !!v || 'Obavezno polje']"
              class="config-input mb-4"
            ></v-select>

            <!-- API Secret -->
            <v-text-field
              v-model="configForm.secret"
              :label="isCreatingConfig ? 'API ključ *' : 'Novi API ključ *'"
              :type="showSecret ? 'text' : 'password'"
              outlined
              dense
              :rules="[v => !!v || 'Obavezno polje']"
              :append-icon="showSecret ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showSecret = !showSecret"
              class="config-input"
            ></v-text-field>

            <!-- Active Toggle (only for create) -->
            <v-switch
              v-if="isCreatingConfig"
              v-model="configForm.active"
              label="Aktivna konfiguracija"
              color="success"
              hide-details
              class="mt-2 config-switch"
            ></v-switch>

            <!-- Send PDF Visualisation Toggle (for create and update) -->
            <v-switch
              v-model="configForm.send_visualisation"
              label="Šalji PDF računa uz eRačun"
              color="success"
              hide-details
              class="mt-2 config-switch"
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-5 pt-2">
          <v-spacer></v-spacer>
          <v-btn
            class="config-cancel-btn"
            @click="closeConfigDialog"
            :disabled="savingConfig"
            text
            elevation="0">
            Odustani
          </v-btn>
          <v-btn
            class="config-save-btn"
            :loading="savingConfig"
            :disabled="!configFormValid"
            @click="saveConfig"
            elevation="0">
            <v-icon left small>{{ isCreatingConfig ? 'mdi-plus' : 'mdi-key-change' }}</v-icon>
            {{ isCreatingConfig ? 'Kreiraj' : 'Ažuriraj' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import state from '@/state'
import { df } from '@/plugins/firebase'
import { eracunApi } from '@/api/eracun'
import CertificateUploadForm from '@/modules/companies/components/CertificateUploadForm.vue'

export default {
  name: 'FiscalizationEracun',
  components: {
    CertificateUploadForm
  },
  data () {
    return {
      // Certificate data
      certData: null,
      passwordVisible: false,
      loading: false,

      // eRacun config data
      eracunConfig: null,
      loadingConfig: false,
      updatingStatus: false,
      updatingSendVisualisation: false,

      // Dialog state
      configDialog: false,
      isCreatingConfig: false,
      savingConfig: false,
      configFormValid: false,
      showSecret: false,

      // Form data
      configForm: {
        provider: 'eposlovanje',
        secret: '',
        active: true,
        send_visualisation: false
      },

      // Provider options
      providerOptions: [
        {
          text: 'Pondi d.o.o. (eposlovanje.hr)',
          value: 'eposlovanje'
        }
      ]
    }
  },
  computed: {
    currentCompany () {
      return state.getCurrentCompany()
    },
    isExpired () {
      if (!this.certData || !this.certData.exp_date) return false
      const now = Math.floor(Date.now() / 1000)
      return now > this.certData.exp_date
    },
    providerDisplayName () {
      if (!this.eracunConfig || !this.eracunConfig.provider) return 'N/A'
      const provider = this.providerOptions.find(p => p.value === this.eracunConfig.provider)
      return provider ? provider.text : this.eracunConfig.provider
    }
  },
  methods: {
    async loadCertificateData () {
      if (!this.currentCompany || !this.currentCompany.id) return

      try {
        this.loading = true
        const docRef = df.doc(`companies/${this.currentCompany.id}`)
        const doc = await docRef.get()

        if (doc.exists && doc.data().cert_data) {
          this.certData = doc.data().cert_data
        } else {
          this.certData = null
        }
      } catch (error) {
        console.error('Error loading certificate data:', error)
        this.certData = null
      } finally {
        this.loading = false
      }
    },
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}.`
    },
    formatTimestamp (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}.${month}.${year}. ${hours}:${minutes}`
    },
    openUploadDialog () {
      this.$refs.certificateUpload.open(this.currentCompany)
    },
    onCertificateUploaded () {
      // Reload certificate data after successful upload
      this.loadCertificateData()
    },

    // eRacun config methods
    async loadEracunConfig () {
      if (!this.currentCompany || !this.currentCompany.id) return

      try {
        this.loadingConfig = true
        const response = await eracunApi.getConfig(this.currentCompany.id)

        if (response.success && response.config) {
          this.eracunConfig = response.config
          // Update company state with the loaded config
          this.updateCompanyState()
        } else {
          this.eracunConfig = null
        }
      } catch (error) {
        console.error('Error loading eRacun config:', error)
        // 404 means config doesn't exist yet - this is expected
        if (error.response?.status !== 404) {
          this.$root.$emit('show-snackbar', {
            text: 'Greška pri učitavanju e-račun konfiguracije',
            color: 'error'
          })
        }
        this.eracunConfig = null
      } finally {
        this.loadingConfig = false
      }
    },

    async toggleActiveStatus () {
      if (!this.currentCompany || !this.currentCompany.id) return

      try {
        this.updatingStatus = true
        await eracunApi.updateConfig(this.currentCompany.id, {
          active: this.eracunConfig.active
        })

        // Update company state to reflect the change
        this.updateCompanyState()

        this.$root.$emit('show-snackbar', {
          text: `Konfiguracija ${this.eracunConfig.active ? 'aktivirana' : 'deaktivirana'}`,
          color: 'success'
        })
      } catch (error) {
        console.error('Error updating active status:', error)
        // Revert the toggle
        this.eracunConfig.active = !this.eracunConfig.active
        this.$root.$emit('show-snackbar', {
          text: error.parsedMessage || 'Greška pri ažuriranju statusa',
          color: 'error'
        })
      } finally {
        this.updatingStatus = false
      }
    },


    async toggleSendVisualisation () {
      if (!this.currentCompany || !this.currentCompany.id) return

      try {
        this.updatingSendVisualisation = true
        await eracunApi.updateConfig(this.currentCompany.id, {
          send_visualisation: this.eracunConfig.send_visualisation
        })

        // Update company state to reflect the change
        this.updateCompanyState()

        this.$root.$emit('show-snackbar', {
          text: `Slanje PDF vizualizacije ${this.eracunConfig.send_visualisation ? 'uključeno' : 'isključeno'}`,
          color: 'success'
        })
      } catch (error) {
        console.error('Error updating send_visualisation status:', error)
        // Revert the toggle
        this.eracunConfig.send_visualisation = !this.eracunConfig.send_visualisation
        this.$root.$emit('show-snackbar', {
          text: error.parsedMessage || 'Greška pri ažuriranju postavke',
          color: 'error'
        })
      } finally {
        this.updatingSendVisualisation = false
      }
    },
    openCreateConfigDialog () {
      this.isCreatingConfig = true
      this.configForm = {
        provider: 'eposlovanje',
        secret: '',
        active: true,
        send_visualisation: false
      }
      this.showSecret = false
      this.configDialog = true
    },

    openUpdateSecretDialog () {
      this.isCreatingConfig = false
      this.configForm = {
        provider: this.eracunConfig.provider,
        secret: '',
        active: this.eracunConfig.active,
        send_visualisation: this.eracunConfig.send_visualisation || false
      }
      this.showSecret = false
      this.configDialog = true
    },

    closeConfigDialog () {
      this.configDialog = false
      if (this.$refs.configForm) {
        this.$refs.configForm.reset()
      }
    },

    updateCompanyState () {
      // Update the company object in state with the new eracun config
      const company = state.getCurrentCompany()
      if (company) {
        company.eracun = { ...this.eracunConfig }
        state.setCurrentCompany(company)
        // Emit event to update SideNav
        this.$root.$emit('eracun-status-changed', this.eracunConfig.active)
      }
    },

    async saveConfig () {
      if (!this.$refs.configForm.validate()) return
      if (!this.currentCompany || !this.currentCompany.id) return

      try {
        this.savingConfig = true

        if (this.isCreatingConfig) {
          // Create new configuration
          const response = await eracunApi.createConfig(this.currentCompany.id, {
            provider: this.configForm.provider,
            secret: this.configForm.secret,
            active: this.configForm.active,
            send_visualisation: this.configForm.send_visualisation
          })

          if (response.success) {
            this.$root.$emit('show-snackbar', {
              text: 'Konfiguracija uspješno kreirana',
              color: 'success'
            })
            this.eracunConfig = response.config
            // Update company state to reflect the change
            this.updateCompanyState()
          }
        } else {
          // Update existing configuration (only secret)
          const response = await eracunApi.updateConfig(this.currentCompany.id, {
            secret: this.configForm.secret,
            send_visualisation: this.configForm.send_visualisation
          })

          if (response.success) {
            this.$root.$emit('show-snackbar', {
              text: 'API ključ uspješno ažuriran',
              color: 'success'
            })
            this.eracunConfig = response.config
          }
        }

        this.closeConfigDialog()
      } catch (error) {
        console.error('Error saving config:', error)
        this.$root.$emit('show-snackbar', {
          text: error.parsedMessage || 'Greška pri spremanju konfiguracije',
          color: 'error'
        })
      } finally {
        this.savingConfig = false
      }
    }
  },
  mounted () {
    this.loadCertificateData()
    this.loadEracunConfig()
  }
}
</script>

<style scoped>
/* Glass Card Style */
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
  height: 100%;
}

/* Card Header with Gradient */
.glass-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 24px;
  position: relative;
}

/* Header Icon */
.header-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

/* Section Title */
.section-title {
  font-size: 22px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.5px !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0;
}

/* Info Labels */
.info-label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: rgba(57, 137, 201, 0.8) !important;
  text-transform: uppercase;
  letter-spacing: 0.5px !important;
  margin-bottom: 6px;
}

/* Info Values */
.info-value {
  font-size: 15px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
  padding: 8px 12px;
  background: rgba(0, 179, 190, 0.05) !important;
  border-radius: 8px;
  border: 1px solid rgba(0, 179, 190, 0.1);
}

/* Serial Number - smaller font */
.serial-number {
  font-size: 12px !important;
  word-break: break-all;
}

/* Password Field */
.password-field {
  flex: 1;
  font-family: monospace;
}

/* Password Toggle Button */
.password-toggle {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  transition: all 0.2s ease !important;
}

.password-toggle:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
}

/* Expired Date */
.expired-date {
  color: rgba(244, 67, 54, 0.9) !important;
  background: rgba(244, 67, 54, 0.08) !important;
  border-color: rgba(244, 67, 54, 0.2) !important;
}

/* Status Chip */
.status-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  backdrop-filter: blur(10px) !important;
}

/* Upload Button */
.upload-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 44px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.upload-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.95) !important;
}

.upload-btn .v-icon {
  font-size: 20px !important;
}

/* No Certificate Content */
.no-cert-content {
  text-align: center;
  padding: 60px 20px;
}

.no-cert-icon {
  color: rgba(0, 179, 190, 0.3) !important;
  filter: drop-shadow(0 4px 8px rgba(0, 179, 190, 0.1));
}

.no-cert-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
}

.no-cert-text {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.7) !important;
  letter-spacing: 0.3px !important;
  line-height: 1.6;
}

/* Placeholder Content Small (for eRacun) */
.placeholder-content-small {
  text-align: center;
  padding: 60px 20px;
}

.placeholder-icon-small {
  color: rgba(0, 179, 190, 0.3) !important;
  filter: drop-shadow(0 4px 8px rgba(0, 179, 190, 0.1));
}

.placeholder-title-small {
  font-size: 20px !important;
  font-weight: 600 !important;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  letter-spacing: 0.5px !important;
  display: inline-block !important;
}

.placeholder-text-small {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.7) !important;
  letter-spacing: 0.3px !important;
  line-height: 1.6;
}

/* Additional styles for eRacun section */
.info-value-inline {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: rgba(70, 70, 80, 0.9) !important;
}

.info-hint {
  font-size: 12px !important;
  color: rgba(70, 70, 80, 0.55) !important;
  margin-top: 4px;
  max-width: 280px;
  line-height: 1.4;
}

.info-label-small {
  font-size: 11px !important;
  font-weight: 600 !important;
  color: rgba(57, 137, 201, 0.6) !important;
  text-transform: uppercase;
  letter-spacing: 0.5px !important;
  margin-bottom: 4px;
}

.info-value-small {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.7) !important;
}
/* Config Dialog Styles */
.config-dialog-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
  overflow: hidden;
}

.config-dialog-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 24px;
  position: relative;
}

.config-dialog-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.5px !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0;
}

.config-input {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
}

.config-input >>> .v-input__control {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 12px !important;
}

.config-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.08) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.config-input >>> .v-input__slot:hover {
  border-color: rgba(0, 179, 190, 0.4) !important;
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.15) !important;
}

.config-input >>> .v-text-field__slot input,
.config-input >>> .v-select__selection {
  color: rgba(70, 70, 80, 0.9) !important;
  font-weight: 500 !important;
}

.config-input >>> .v-label {
  color: rgba(57, 137, 201, 0.7) !important;
  font-weight: 500 !important;
}

.config-switch >>> .v-input--selection-controls__input {
  color: #3989C9 !important;
}

.config-switch >>> .v-label {
  color: rgba(70, 70, 80, 0.9) !important;
  font-weight: 500 !important;
  font-size: 15px !important;
}

.config-cancel-btn {
  background: transparent !important;
  color: rgba(70, 70, 80, 0.7) !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 44px !important;
  box-shadow: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.config-cancel-btn:hover {
  background: rgba(70, 70, 80, 0.08) !important;
  color: rgba(70, 70, 80, 0.9) !important;
}

.config-save-btn {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 44px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.config-save-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 1) !important;
}

.config-save-btn .v-icon {
  font-size: 18px !important;
  margin-right: 8px !important;
}
</style>
